<script lang="ts">import Text from '../Typography/Text/Text.svelte';
const { eyebrowText = '', eyebrowVariant = 'regular', isEyebrowInHeading = false, headingText = '', headingLevel = 'h2', headingVariant = '800', bodyText = '', bodyVariant = '100', ctaLayout = 'row', alignment = 'left', ctaItems, class: className = '', ...rest } = $props();
const numericEyebrowVariant = (eyebrowVariant === 'regular' ? '50' : '200');
const alignItemsMap = {
    left: 'flex-start',
    right: 'flex-end',
    center: 'center',
};
</script>

<div
  {...rest}
  class="TextStack {className}"
  style:--text-stack-title-margin-bottom="var(--text-variant-{headingVariant}-spacing-below)"
  style:--text-stack-eyebrow-margin-bottom="var(--text-variant-{numericEyebrowVariant}-spacing-below)"
  style:--text-stack-text-margin-bottom="var(--text-variant-{bodyVariant}-spacing-below)"
  style:--text-stack-cta-layout={ctaLayout}
  style:--text-stack-cta-gap={ctaLayout === 'row' ? 'var(--space-20)' : 'var(--space-10)'}
  style:--text-stack-flex-alignment={alignItemsMap[alignment]}
  style:--text-stack-text-alignment={alignment}
  style:--text-stack-optical-text-indent={alignment === 'left' ? '-0.1ch' : '0'}
>
  {#if eyebrowText || headingText || bodyText}
    <!-- Need an extra wrapper element because Hero modifies TextStack where the CTA slot is in a different column -->
    <div
      class="TextStack__wrapper"
      class:TextStack__wrapper--with-text={bodyText}
      class:TextStack__wrapper--without-text={!bodyText}
    >
      {#if eyebrowText || headingText}
        <svelte:element this={isEyebrowInHeading ? headingLevel : 'div'} class="TextStack__title">
          {#if eyebrowText}
            <Text
              class="TextStack__eyebrow TextStack__eyebrow--{eyebrowVariant}"
              as="span"
              variant={numericEyebrowVariant}
            >
              {eyebrowText}
            </Text>
          {/if}
          {#if headingText}
            <Text class="TextStack__heading" as={isEyebrowInHeading ? 'span' : headingLevel} variant={headingVariant}>
              {headingText}
            </Text>
          {/if}
        </svelte:element>
      {/if}
      {#if typeof bodyText === 'function'}
        {@render bodyText()}
      {:else if bodyText}
        <Text class="TextStack__text" variant={bodyVariant}>
          {@html bodyText}
        </Text>
      {/if}
    </div>
  {/if}

  {#if ctaItems}
    <div class="TextStack__links">
      {@render ctaItems()}
    </div>
  {/if}
</div>

<style>/**
 * Do not edit directly, this file was auto-generated.
 * Wed, 13 Aug 2025 22:02:02 GMT
 */
/**
 We use rem units for media queries to promote accessible layouts. By utilizing rem units, the computed values in media queries are determined by the user's browser-defined font size rather than our global 'font-size: 62.5%' reset. This approach ensures that our design adapts to individual user settings, enhancing accessibility. 
 */
@layer design-system-components.pwds {
  .TextStack {
    display: flex;
    flex-direction: column;
    align-items: var(--text-stack-flex-alignment);
    text-align: var(--text-stack-text-alignment);
  }
  .TextStack__wrapper.TextStack__wrapper--with-text:not(:has(*)), .TextStack__wrapper.TextStack__wrapper--without-text:not(:has(*)) {
    display: none;
  }
  .TextStack__wrapper.TextStack__wrapper--with-text:not(:has(*)) + .TextStack__links, .TextStack__wrapper.TextStack__wrapper--without-text:not(:has(*)) + .TextStack__links {
    margin-top: var(--space-none);
  }
  .TextStack__wrapper--with-text + .TextStack__links {
    margin-top: var(--text-stack-text-margin-bottom);
  }
  .TextStack__wrapper--with-text + .TextStack__links:global(:has(.Action--button, .Action--button-alt)) {
    margin-top: var(--space-30);
  }
  .TextStack__wrapper--without-text + .TextStack__links {
    margin-top: var(--text-stack-title-margin-bottom);
  }
  .TextStack__title,
  .TextStack :global(.TextStack__heading) {
    margin-bottom: var(--space-none);
  }
  .TextStack__title {
    line-height: 0;
  }
  .TextStack__title + :global(.TextStack__text) {
    margin-top: var(--text-stack-title-margin-bottom);
  }
  .TextStack__links {
    display: flex;
    flex-direction: var(--text-stack-cta-layout);
    flex-wrap: wrap;
    justify-content: center;
    gap: var(--text-stack-cta-gap);
  }
  @media (max-width: 23.3125rem) {
    .TextStack__links :global(.Action),
    .TextStack__links :global(.Action) :global(.Action__content-wrapper) {
      width: 100%;
    }
    .TextStack__links :global(.Action) :global(.Action__content-wrapper) {
      max-width: none;
    }
    .TextStack__links > :global(*) {
      flex: 1 1 max-content;
    }
  }
  .TextStack :global(.TextStack__eyebrow),
  .TextStack :global(.TextStack__heading) {
    display: block;
  }
  .TextStack :global(.TextStack__eyebrow.TextStack__eyebrow--regular),
  .TextStack :global(.TextStack__eyebrow.TextStack__eyebrow.TextStack__eyebrow--hero) {
    max-width: none;
  }
  .TextStack :global(.TextStack__eyebrow) {
    margin-bottom: var(--text-stack-eyebrow-margin-bottom);
  }
  .TextStack :global(.TextStack__eyebrow--regular) {
    color: var(--color-neutral-300);
    text-transform: uppercase;
  }
  @media (min-width: 64rem) {
    .TextStack :global(.TextStack__eyebrow--regular) {
      --font-family: var(--text-font-family);
      --color: var(--color-text);
      --font-size: var(--text-variant-100-font-size);
      --font-weight: var(--text-variant-100-font-weight);
      --letter-spacing: var(--text-variant-100-letter-spacing);
      --line-height: var(--text-variant-100-line-height);
      --max-width: var(--text-variant-100-max-width);
      font-family: var(--font-family);
      font-size: var(--font-size);
      font-weight: var(--font-weight);
      letter-spacing: var(--letter-spacing);
      line-height: var(--line-height);
      max-width: var(--max-width);
    }
  }
  @media (min-width: 64rem) {
    .TextStack :global(.TextStack__eyebrow--hero) {
      --font-family: var(--text-font-family);
      --color: var(--color-text);
      --font-size: var(--text-variant-200-font-size);
      --font-weight: var(--text-variant-200-font-weight);
      --letter-spacing: var(--text-variant-200-letter-spacing);
      --line-height: var(--text-variant-200-line-height);
      --max-width: var(--text-variant-200-max-width);
      font-family: var(--font-family);
      font-size: var(--font-size);
      font-weight: var(--font-weight);
      letter-spacing: var(--letter-spacing);
      line-height: var(--line-height);
      max-width: var(--max-width);
    }
  }
  .TextStack :global(.TextStack__heading),
  .TextStack :global(.TextStack__text) {
    text-indent: var(--text-stack-optical-text-indent);
  }
}</style>
